import { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Upload, X, FileText, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { SectionHeader } from "./Home";

const INITIAL = {
  full_name: "", email: "", phone: "", company: "", client_type: "",
  property_address: "", city: "", state: "", parcel_number: "",
  property_type: "", service_needed: "", purpose: "", turnaround: "", additional_notes: "",
};

const CLIENT_TYPES = ["Individual Buyer", "Law Firm", "Real Estate Developer", "Bank / Financial Institution", "Property Investor", "Government Agency", "Other"];
const PROPERTY_TYPES = ["Residential Flat / Apartment", "Independent House / Bungalow", "Residential Plot", "Agricultural Land", "Commercial Property", "Industrial Property", "Other"];
const SERVICES_NEEDED = ["Title Search & Verification", "Land Records Retrieval", "Encumbrance Certificate Report", "Legal Due Diligence Support", "Property Document Verification", "Ownership History Report", "Not Sure — Advise Me"];
const TURNAROUNDS = ["Standard (7–10 working days)", "Priority (3–5 working days)", "Urgent (24–72 hours)"];

const Field = ({ label, id, required, children }) => (
  <div>
    <label className="tt-label" htmlFor={id}>{label}{required && " *"}</label>
    {children}
  </div>
);

const ReportTeaser = () => (
  <aside data-testid="report-teaser" className="lg:sticky lg:top-36 h-fit">
    <div className="relative overflow-hidden border border-[#C1A74A]/60 shadow-[0_24px_60px_rgba(17,56,53,0.15)]">
      <img
        src="/sample-report.png"
        alt="Sample Terraa Score due diligence report (blurred preview)"
        className="w-full h-auto blur-[5px] scale-[1.04] select-none pointer-events-none"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#113835]/80" />
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8">
        <div className="w-12 h-12 bg-[#113835] text-[#C1A74A] flex items-center justify-center mb-5 shadow-lg">
          <Lock size={20} strokeWidth={1.5} />
        </div>
        <p className="font-heading text-[0.68rem] tracking-[0.28em] uppercase text-[#113835] bg-[#F4D387] px-4 py-1.5 font-semibold">
          Sample Terraa Score Report
        </p>
        <p className="mt-4 font-accent italic text-2xl text-white drop-shadow-md">
          Your property, scored out of 1000.
        </p>
        <p className="mt-3 text-xs text-white/90 font-light leading-relaxed max-w-xs drop-shadow">
          Source-verified against government portals, with every red flag documented.
          Submit your request to receive the full, unblurred report.
        </p>
      </div>
    </div>
    <p className="mt-4 text-[0.68rem] text-gray-400 font-light text-center tracking-wide uppercase">
      Delivered as a branded PDF · Court & bank ready
    </p>
  </aside>
);

export default function RequestTitleSearchPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => ({ ...INITIAL, property_address: params.get("address") || "" }));
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addFiles = (list) => {
    const incoming = Array.from(list).slice(0, 5 - files.length);
    setFiles([...files, ...incoming]);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const document_ids = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd);
        document_ids.push(data.id);
      }
      await api.post("/title-search", { ...form, document_ids });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="title-search-success" className="py-32 md:py-44 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#113835] text-[#C1A74A] flex items-center justify-center mx-auto mb-10">
            <CheckCircle2 size={32} strokeWidth={1.25} />
          </div>
          <p className="tt-eyebrow mb-4">Request Received</p>
          <h1 className="font-heading text-3xl md:text-5xl font-medium text-[#113835] tracking-tight">
            Your property verification request is with our experts.
          </h1>
          <p className="mt-6 text-base text-gray-600 font-light leading-relaxed">
            A case manager will review your details and contact you within one business day to confirm scope, plan, and timeline.
            Expert review typically takes 3–7 working days depending on record availability.
          </p>
          <button
            data-testid="title-search-new-request"
            onClick={() => { setForm(INITIAL); setFiles([]); setSubmitted(false); }}
            className="tt-btn-outline mt-12"
          >
            Submit Another Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="title-search-page">
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_380px] gap-16 items-start">
          <div>
            <SectionHeader
              eyebrow="Request Property Verification"
              title="Tell us about the property."
              sub="The more detail you share, the faster our experts can begin. Fields marked * are required. Plans start at ₹2,999 — see pricing."
              className="mb-8"
            />
            <div className="grid sm:grid-cols-2 gap-6 mb-12" data-testid="ts-info-panels">
              <div className="border border-[#113835]/10 bg-[#F9FAFA] p-6">
                <p className="font-heading text-xs uppercase tracking-[0.18em] text-[#1D504C] mb-3">What you'll need</p>
                <p className="text-sm text-gray-600 font-light leading-relaxed">
                  The property address or survey number, plus any papers you already hold — sale deeds, 7/12 extracts,
                  tax receipts, allotment letters, or layout papers. Missing documents are fine; finding gaps is part of the review.
                </p>
              </div>
              <div className="border border-[#113835]/10 bg-[#F9FAFA] p-6">
                <p className="font-heading text-xs uppercase tracking-[0.18em] text-[#1D504C] mb-3">What happens next</p>
                <p className="text-sm text-gray-600 font-light leading-relaxed">
                  A case manager confirms scope within one business day. Expert review takes 3–7 working days depending
                  on plan, property type, and record availability. You receive a Land Rating and PDF report.
                </p>
              </div>
            </div>
          <form onSubmit={onSubmit} data-testid="title-search-form" className="space-y-14">
            <fieldset className="space-y-8">
              <legend className="font-heading text-lg font-medium text-[#113835] border-b border-[#C1A74A]/40 pb-3 w-full">Your Details</legend>
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label="Full Name" id="ts-name" required>
                  <input id="ts-name" data-testid="ts-input-name" className="tt-input" required value={form.full_name} onChange={set("full_name")} placeholder="Your name" />
                </Field>
                <Field label="Email Address" id="ts-email" required>
                  <input id="ts-email" data-testid="ts-input-email" className="tt-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" />
                </Field>
                <Field label="Phone Number" id="ts-phone" required>
                  <input id="ts-phone" data-testid="ts-input-phone" className="tt-input" required value={form.phone} onChange={set("phone")} placeholder="+91 ..." />
                </Field>
                <Field label="Company / Organization" id="ts-company">
                  <input id="ts-company" data-testid="ts-input-company" className="tt-input" value={form.company} onChange={set("company")} placeholder="Optional" />
                </Field>
                <Field label="Client Type" id="ts-client-type" required>
                  <select id="ts-client-type" data-testid="ts-select-client-type" className="tt-input" required value={form.client_type} onChange={set("client_type")}>
                    <option value="" disabled>Select client type</option>
                    {CLIENT_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
            </fieldset>

            <fieldset className="space-y-8">
              <legend className="font-heading text-lg font-medium text-[#113835] border-b border-[#C1A74A]/40 pb-3 w-full">Property Details</legend>
              <Field label="Property Address" id="ts-address" required>
                <input id="ts-address" data-testid="ts-input-address" className="tt-input" required value={form.property_address} onChange={set("property_address")} placeholder="Full property address" />
              </Field>
              <div className="grid sm:grid-cols-3 gap-8">
                <Field label="City" id="ts-city" required>
                  <input id="ts-city" data-testid="ts-input-city" className="tt-input" required value={form.city} onChange={set("city")} placeholder="City" />
                </Field>
                <Field label="State / Region" id="ts-state" required>
                  <input id="ts-state" data-testid="ts-input-state" className="tt-input" required value={form.state} onChange={set("state")} placeholder="State" />
                </Field>
                <Field label="Parcel / Plot / Survey No." id="ts-parcel">
                  <input id="ts-parcel" data-testid="ts-input-parcel" className="tt-input" value={form.parcel_number} onChange={set("parcel_number")} placeholder="If known" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <Field label="Type of Property" id="ts-property-type" required>
                  <select id="ts-property-type" data-testid="ts-select-property-type" className="tt-input" required value={form.property_type} onChange={set("property_type")}>
                    <option value="" disabled>Select property type</option>
                    {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Service Needed" id="ts-service" required>
                  <select id="ts-service" data-testid="ts-select-service" className="tt-input" required value={form.service_needed} onChange={set("service_needed")}>
                    <option value="" disabled>Select service</option>
                    {SERVICES_NEEDED.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Purpose of Request" id="ts-purpose">
                  <input id="ts-purpose" data-testid="ts-input-purpose" className="tt-input" value={form.purpose} onChange={set("purpose")} placeholder="e.g. Purchase, loan, litigation" />
                </Field>
                <Field label="Required Turnaround" id="ts-turnaround">
                  <select id="ts-turnaround" data-testid="ts-select-turnaround" className="tt-input" value={form.turnaround} onChange={set("turnaround")}>
                    <option value="" disabled>Select turnaround</option>
                    {TURNAROUNDS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
            </fieldset>

            <fieldset className="space-y-6">
              <legend className="font-heading text-lg font-medium text-[#113835] border-b border-[#C1A74A]/40 pb-3 w-full">Supporting Documents</legend>
              <p className="text-sm text-gray-500 font-light">Sale deeds, 7/12 extracts, agreements — PDF, DOC, or images. Up to 5 files, 10 MB each. Optional.</p>
              <p className="text-xs text-gray-400 font-light" data-testid="ts-privacy-note">
                Your documents are kept confidential, used only for this verification, and accessible only to your assigned expert team.
              </p>
              <button
                type="button"
                data-testid="ts-upload-trigger"
                onClick={() => fileRef.current?.click()}
                disabled={files.length >= 5}
                className="w-full border border-dashed border-[#113835]/30 hover:border-[#C1A74A] transition-colors py-10 flex flex-col items-center gap-3 text-[#1D504C] disabled:opacity-50"
              >
                <Upload size={26} strokeWidth={1.25} className="text-[#C1A74A]" />
                <span className="font-heading text-xs uppercase tracking-[0.16em]">Click to upload documents</span>
              </button>
              <input ref={fileRef} data-testid="ts-file-input" type="file" multiple accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
              {files.length > 0 && (
                <ul className="space-y-2" data-testid="ts-file-list">
                  {files.map((f, i) => (
                    <li key={`${f.name}-${i}`} className="flex items-center justify-between border border-[#113835]/10 px-4 py-3 text-sm">
                      <span className="flex items-center gap-3 text-[#113835]"><FileText size={16} strokeWidth={1.25} className="text-[#C1A74A]" />{f.name}</span>
                      <button type="button" data-testid={`ts-remove-file-${i}`} onClick={() => setFiles(files.filter((_, idx) => idx !== i))} aria-label={`Remove ${f.name}`} className="text-gray-400 hover:text-red-600 transition-colors">
                        <X size={16} strokeWidth={1.5} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <Field label="Additional Notes" id="ts-notes">
                <textarea id="ts-notes" data-testid="ts-input-notes" className="tt-input" value={form.additional_notes} onChange={set("additional_notes")} placeholder="Anything else our researchers should know..." />
              </Field>
            </fieldset>

            <button data-testid="ts-submit-button" type="submit" disabled={submitting} className="tt-btn-gold w-full justify-center disabled:opacity-60">
              {submitting ? "Submitting Request..." : "Submit Verification Request"}
            </button>
            <p className="text-xs text-gray-400 font-light text-center -mt-8">
              Submitting a request is free. Verification plans start from ₹2,999 — <a href="/pricing" className="underline hover:text-[#C1A74A]">see pricing</a>. No payment is taken until scope is confirmed.
            </p>
          </form>
          </div>
          <ReportTeaser />
        </div>
      </section>
    </div>
  );
}
